<script>
  import { token } from "./store/token.js";
  import { canAccess, permissions } from "./store/permissions.js";
  import { handleApiResponse } from "./api/request.js";
  import { onMount } from "svelte";
  import UserDetail from './UserDetail.svelte';
  import { UPGRADE_PLAN_OPTIONS } from "./lib/planOptions.js";

  export let workspaceId = null;
  export let onWorkspaceDeleted = undefined;

  let workspace = null;
  let members = [];
  let subscription = null;
  let audioList = [];
  let loading = true;
  let error = null;
  let selectedAudio = null;
  let showAudioModal = false;

  // Tabbed layout (mirrors UserDetail)
  let activeTab = "overview";
  const TABS = [
    { id: "overview", label: "Overview" },
    { id: "members", label: "Members" },
    { id: "audio", label: "Audio Files" },
  ];
  let showUserDetail = false;
  let selectedUserId = null;

  // Upgrade form state
  let showUpgradeForm = false;
  let selectedPlanId = "workspace-team-seats-yearly";
  let planQuantity = 5;
  let expiresAt = "";
  let upgradeLoading = false;
  let upgradeError = null;
  let upgradeSuccess = null;

  // Trial extension state
  let showTrialForm = false;
  let extendDays = 14;
  let extendDate = "";
  let extendMode = "days";
  let trialLoading = false;

  // Delete workspace state
  let showDeleteConfirm = false;
  let deleteLoading = false;
  let deleteError = null;

  const fetchWorkspaceDetail = async () => {
    if (!workspaceId) return;
    
    try {
      loading = true;
      error = null;
      const response = await fetch(`${process.env.API_ROOT}/admin/workspace/${workspaceId}/detail`, {
        headers: {
          Authorization: "Bearer " + $token,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      workspace = data.workspace;
      members = data.member_list;
      subscription = data.subscription;
      audioList = data.audio_list;
      if (data.device_platform_list) {
        workspace.device_platform_list = data.device_platform_list;
      }
    } catch (e) {
      error = e.message;
      console.error("Error fetching workspace details:", e);
    } finally {
      loading = false;
    }
  };

  $: if (workspaceId) {
    fetchWorkspaceDetail();
  }

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "-";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case "owner":
        return "badge-owner";
      case "viewer":
        return "badge-viewer";
      default:
        return "";
    }
  };

  const getSubscriptionStatusClass = (status) => {
    switch (status) {
      case "active":
        return "status-active";
      case "cancelled":
        return "status-cancelled";
      default:
        return "";
    }
  };

  const handleAudioClick = (audio) => {
    selectedAudio = audio;
    showAudioModal = true;
  };

  const closeModal = () => {
    showAudioModal = false;
    selectedAudio = null;
  };

  function handleUserClick(userId) {
    selectedUserId = userId;
    showUserDetail = true;
  }

  function closeUserDetail() {
    showUserDetail = false;
    selectedUserId = null;
  }

  // Add handler for openWorkspace event from UserDetail
  function handleOpenWorkspace(event) {
    const { workspaceId: newWorkspaceId } = event.detail;
    // Switch to the new workspace detail view
    selectedUserId = null;
    showUserDetail = false;
    // If you want to update the current workspaceId, do so here:
    // workspaceId = newWorkspaceId;
    // But since workspaceId is a prop, you may need to dispatch or lift state if this is not the top-level component.
    // For now, let's assume we want to show the new workspace detail in this component:
    // (If workspaceId is not writable, you may need to lift state to a parent component.)
    // If you want to reload, you can do:
    if (workspaceId !== newWorkspaceId) {
      workspaceId = newWorkspaceId;
      fetchWorkspaceDetail();
    }
  }

  const handleUpgrade = async () => {
    upgradeLoading = true;
    upgradeError = null;
    upgradeSuccess = null;

    try {
      const body = {
        plan_id: selectedPlanId,
        plan_quantity: parseInt(planQuantity, 10),
      };
      if (expiresAt) {
        body.expires_at = new Date(expiresAt).toISOString();
      }

      const response = await fetch(
        `${process.env.API_ROOT}/admin/workspace/${workspaceId}/upgrade`,
        {
          method: "POST",
          headers: {
            Authorization: "Bearer " + $token,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      const data = await handleApiResponse(response);
      upgradeSuccess = data.message || "Workspace upgraded successfully";
      showUpgradeForm = false;
      await fetchWorkspaceDetail();
    } catch (e) {
      upgradeError = e.message;
    } finally {
      upgradeLoading = false;
    }
  };

  const handleExtendTrial = async () => {
    trialLoading = true;
    upgradeError = null;
    upgradeSuccess = null;

    try {
      const body = {};
      if (extendMode === "days") {
        body.days = parseInt(extendDays, 10);
      } else {
        body.expires_at = new Date(extendDate).toISOString();
      }

      const response = await fetch(
        `${process.env.API_ROOT}/admin/workspace/${workspaceId}/trial/extend`,
        {
          method: "POST",
          headers: {
            Authorization: "Bearer " + $token,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      const data = await handleApiResponse(response);
      upgradeSuccess = data.message || "Trial extended successfully";
      showTrialForm = false;
      await fetchWorkspaceDetail();
    } catch (e) {
      upgradeError = e.message;
    } finally {
      trialLoading = false;
    }
  };

  const TRIAL_SHORTCUTS = [
    { label: "+14 days", body: { days: 14 } },
    { label: "+3 months", body: { months: 3 } },
    { label: "+6 months", body: { months: 6 } },
    { label: "+12 months", body: { months: 12 } },
  ];

  const extendTrialBy = async (body) => {
    trialLoading = true;
    upgradeError = null;
    upgradeSuccess = null;

    try {
      const response = await fetch(
        `${process.env.API_ROOT}/admin/workspace/${workspaceId}/trial/extend`,
        {
          method: "POST",
          headers: {
            Authorization: "Bearer " + $token,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      const data = await handleApiResponse(response);
      upgradeSuccess = data.message || "Trial extended successfully";
      showTrialForm = false;
      await fetchWorkspaceDetail();
    } catch (e) {
      upgradeError = e.message;
    } finally {
      trialLoading = false;
    }
  };

  // --- Trial extension confirmation + anti-stacking ---
  let pendingExtension = null; // the shortcut awaiting confirmation: { label, body }
  const requestExtension = (sc) => { pendingExtension = sc; };
  const cancelExtension = () => { pendingExtension = null; };
  const confirmExtension = async () => {
    const sc = pendingExtension;
    pendingExtension = null;
    if (sc) await extendTrialBy(sc.body);
  };

  // An admin-applied trial extension is "active" while its expiry is still in
  // the future. While active, the shortcut buttons are disabled so a second
  // extension can't be stacked until the current one expires.
  $: activeExtension = (() => {
    if (!subscription || !subscription.is_trial) return null;
    const sid = subscription.subscription_id || "";
    if (!/^admin-trial-extended-/.test(sid)) return null;
    const expires = subscription.expires_at ? new Date(subscription.expires_at) : null;
    if (!expires || isNaN(expires.getTime()) || expires <= new Date()) return null;
    const daysLeft = Math.max(0, Math.ceil((expires.getTime() - Date.now()) / 86400000));
    return {
      expires,
      daysLeft,
      extendedAt: subscription.updated_at || subscription.started_at || null,
      extendedByEmail: subscription.extended_by_email || null,
    };
  })();

  const closeDeleteConfirm = () => {
    showDeleteConfirm = false;
    deleteError = null;
  };

  const handleDeleteWorkspace = async () => {
    if (!workspaceId) return;
    deleteLoading = true;
    deleteError = null;
    try {
      const res = await fetch(`${process.env.API_ROOT}/admin/workspace/${workspaceId}`, {
        method: "DELETE",
        headers: { Authorization: "Bearer " + $token },
      });
      if (res.status === 204) {
        closeDeleteConfirm();
        onWorkspaceDeleted?.();
        return;
      }
      const body = await res.json().catch(() => ({}));
      if (res.status === 401) {
        permissions.clear();
        token.set("");
        deleteError = "Session expired. Please log in again.";
        return;
      }
      if (res.status === 403) {
        deleteError = "You don't have permission to delete workspaces.";
        return;
      }
      if (res.status === 404) {
        deleteError = body.message ?? "Workspace not found.";
        return;
      }
      if (res.status === 410) {
        deleteError = "This workspace has already been deleted.";
        onWorkspaceDeleted?.();
        return;
      }
      deleteError = body.message ?? "Failed to delete workspace.";
    } finally {
      deleteLoading = false;
    }
  };
</script>

{#if showUserDetail && selectedUserId}
  <UserDetail userId={selectedUserId} on:openWorkspace={handleOpenWorkspace} />
{:else}
<div class="container">
  {#if loading}
    <div class="loading-container">
      <div class="spinner"></div>
      <div>Loading workspace details...</div>
    </div>
  {:else if error}
    <div class="error-container">
      <div class="error-message">
        Failed to load workspace details: {error}
      </div>
      <button class="retry-button" on:click={fetchWorkspaceDetail}>
        Try Again
      </button>
    </div>
  {:else if workspace}
    <header class="ud-header">
      <div class="ud-identity">
        <div class="ud-avatar">
          {#if workspace.image_url}
            <img src={workspace.image_url} alt={workspace.title} />
          {:else}
            <div class="avatar-placeholder">
              {workspace.title && workspace.title.length > 0 ? workspace.title.charAt(0) : "?"}
            </div>
          {/if}
        </div>
        <div class="ud-identity-text">
          <div class="ud-name">{workspace.title}</div>
          {#if workspace.description}
            <div class="ud-sub">{workspace.description}</div>
          {/if}
          <div class="ud-chips">
            <span class="chip">ID: {workspace.string_id}</span>
            <span class="chip">Created {formatDate(workspace.created_at)}</span>
            {#if subscription}
              <span
                class="chip"
                class:chip-ok={subscription.status === 'active' && !subscription.is_trial}
                class:chip-warn={subscription.is_trial || subscription.status !== 'active'}
              >
                {subscription.is_trial ? 'Trial' : subscription.status}
              </span>
            {/if}
            {#if workspace.deleted_at}
              <span class="chip chip-warn">Deleted {formatDate(workspace.deleted_at)}</span>
            {/if}
          </div>
        </div>
      </div>
    </header>

    <nav class="ud-tabs">
      {#each TABS as tab}
        <button
          class="ud-tab"
          class:active={activeTab === tab.id}
          on:click={() => (activeTab = tab.id)}
        >
          {tab.label}
          {#if tab.id === "members"}
            <span class="tab-count">{members.length}</span>
          {:else if tab.id === "audio"}
            <span class="tab-count">{audioList.length}</span>
          {/if}
        </button>
      {/each}
    </nav>

    {#if activeTab === "overview"}
    <div class="workspace-info-card card">
      <h2>Workspace Info</h2>
      <div class="info-grid">
        {#if subscription || workspace.subscription?.plan_title}
          <div class="info-row">
            <span class="label">Plan:</span>
            <span class="value">
              {#if subscription}
                {subscription.is_trial ? "Pro Trial" : (subscription.plan_title || subscription.plan_id)}
                {#if subscription.is_trial}
                  <span class="trial-badge">Trial</span>
                {/if}
              {:else}
                {workspace.subscription.plan_title}
              {/if}
            </span>
          </div>
        {/if}
        {#if workspace.subscription?.plan_description}
          <div class="info-row">
            <span class="label">Plan Description:</span>
            <span class="value">{workspace.subscription.plan_description}</span>
          </div>
        {/if}
        {#if workspace.info?.seats_limit}
          <div class="info-row">
            <span class="label">Seats Used:</span>
            <span class="value">{workspace.info.seats_used ?? '-'} / {workspace.info.seats_limit}</span>
          </div>
        {/if}
        {#if workspace.info?.storage?.audio_count}
          <div class="info-row">
            <span class="label">Audio Files:</span>
            <span class="value">{workspace.info.storage.audio_count}</span>
          </div>
        {/if}
        {#if workspace.info?.storage_used_gb}
          <div class="info-row">
            <span class="label">Storage Used:</span>
            <span class="value">{workspace.info.storage_used_gb.toFixed(2)} GB / {workspace.info.storage_limit_gb} GB ({workspace.info.storage_used_percent}%)</span>
          </div>
        {/if}
        {#if workspace.enabled_features?.length}
          <div class="info-row">
            <span class="label">Enabled Features:</span>
            <span class="value features-list">
              {#each workspace.enabled_features as feature}
                <span class="feature-badge">{feature.replace(/_/g, ' ')}</span>
              {/each}
            </span>
          </div>
        {/if}
        {#if workspace.device_platform_list && workspace.device_platform_list.length}
          <div class="info-row">
            <span class="label">Platforms:</span>
            <span class="value features-list">
              {#each workspace.device_platform_list as platform}
                <span class="feature-badge">{platform}</span>
              {/each}
            </span>
          </div>
        {/if}
      </div>
    </div>

      <div class="card subscription-card">
        <h2>Subscription</h2>
        
        {#if upgradeSuccess}
          <div class="alert alert-success">
            {upgradeSuccess}
            <button class="dismiss-btn" on:click={() => upgradeSuccess = null}>×</button>
          </div>
        {/if}
        {#if upgradeError}
          <div class="alert alert-error">
            {upgradeError}
            <button class="dismiss-btn" on:click={() => upgradeError = null}>×</button>
          </div>
        {/if}

        {#if subscription}
          <div class="subscription-info">
            <div class="info-row">
              <span class="label">Plan:</span>
              <span class="value">
                {subscription.is_trial ? "Pro Trial" : (subscription.plan_title || subscription.plan_id)}
                {#if subscription.is_trial}
                  <span class="trial-badge">Trial</span>
                {/if}
              </span>
            </div>
            <div class="info-row">
              <span class="label">Billing:</span>
              <span class="value">
                {subscription.plan_id?.includes('monthly') ? 'Monthly' : 'Yearly'}
              </span>
            </div>
            <div class="info-row">
              <span class="label">Status:</span>
              <span class="value status-badge" class:status-active={subscription.status === 'active'} class:status-cancelled={subscription.status === 'cancelled'}>
                {subscription.status}
              </span>
            </div>
            {#if subscription.plan_quantity}
              <div class="info-row">
                <span class="label">Seats:</span>
                <span class="value">{subscription.plan_quantity}</span>
              </div>
            {/if}
            {#if subscription.is_trial !== undefined}
              <div class="info-row">
                <span class="label">Trial:</span>
                <span class="value">{subscription.is_trial ? 'Yes' : 'No'}</span>
              </div>
            {/if}
            <div class="info-row">
              <span class="label">Started:</span>
              <span class="value">{formatDate(subscription.started_at)}</span>
            </div>
            {#if subscription.expires_at}
              <div class="info-row">
                <span class="label">Expires:</span>
                <span class="value">{formatDate(subscription.expires_at)}</span>
              </div>
            {/if}
            {#if subscription.next_billing_at}
              <div class="info-row">
                <span class="label">Next Billing:</span>
                <span class="value">{formatDate(subscription.next_billing_at)}</span>
              </div>
            {/if}
            {#if subscription.cancelled_at}
              <div class="info-row">
                <span class="label">Cancelled:</span>
                <span class="value">{formatDate(subscription.cancelled_at)}</span>
              </div>
            {/if}
          </div>
        {:else}
          <div class="no-subscription">No subscription information available</div>
        {/if}

        {#if $canAccess.upgrades_section}
          <div class="upgrade-actions">
            {#if $canAccess.upgrades}
            <button class="action-btn" class:active={showUpgradeForm} on:click={() => { showUpgradeForm = !showUpgradeForm; showTrialForm = false; }}>
              {showUpgradeForm ? 'Cancel' : 'Upgrade Plan'}
            </button>
            {/if}
            <button class="action-btn secondary" class:active={showTrialForm} on:click={() => { showTrialForm = !showTrialForm; showUpgradeForm = false; }}>
              {showTrialForm ? 'Cancel' : 'Extend Trial'}
            </button>
          </div>

          {#if showUpgradeForm && $canAccess.upgrades}
            <div class="upgrade-form">
              <h3>Upgrade Workspace</h3>
              <div class="form-group">
                <label for="plan">Plan</label>
                <select id="plan" bind:value={selectedPlanId} class="form-select">
                  {#each UPGRADE_PLAN_OPTIONS as plan}
                    <option value={plan.value}>{plan.label}</option>
                  {/each}
                </select>
              </div>
              <div class="form-group">
                <label for="seats">Seat Quantity</label>
                <input id="seats" type="number" min="1" bind:value={planQuantity} class="form-input" />
              </div>
              <div class="form-group">
                <label for="expires">Expiration (optional)</label>
                <input id="expires" type="datetime-local" bind:value={expiresAt} class="form-input" />
                <span class="form-hint">Leave empty for no expiration</span>
              </div>
              <button class="submit-btn" on:click={handleUpgrade} disabled={upgradeLoading}>
                {#if upgradeLoading}
                  <span class="btn-spinner"></span> Upgrading...
                {:else}
                  Apply Upgrade
                {/if}
              </button>
            </div>
          {/if}

          {#if showTrialForm}
            <div class="upgrade-form">
              <h3>Extend Trial</h3>
              <div class="shortcut-row">
                {#each TRIAL_SHORTCUTS as sc}
                  <button
                    type="button"
                    class="shortcut-button"
                    on:click={() => requestExtension(sc)}
                    disabled={trialLoading || !!activeExtension}
                  >
                    {sc.label}
                  </button>
                {/each}
              </div>

              {#if activeExtension}
                <div class="extension-info">
                  <div class="ext-line">Date extended: <strong>{formatDate(activeExtension.extendedAt)}</strong></div>
                  {#if activeExtension.extendedByEmail}
                    <div class="ext-line">Extended by: <strong>{activeExtension.extendedByEmail}</strong></div>
                  {/if}
                  <div class="ext-line">Time remaining on extension: <strong>{activeExtension.daysLeft} {activeExtension.daysLeft === 1 ? 'day' : 'days'}</strong></div>
                </div>
              {/if}
              <div class="ext-note">Note: Only one extension can be applied at a time</div>
              <!-- Marketing can only extend via the shortcut buttons above;
                   the manual method/day/date controls are hidden for them. -->
              {#if $permissions.role !== 'marketing'}
              <div class="form-group">
                <label>Extension Method</label>
                <div class="radio-group">
                  <label class="radio-label">
                    <input type="radio" bind:group={extendMode} value="days" /> By Days
                  </label>
                  <label class="radio-label">
                    <input type="radio" bind:group={extendMode} value="date" /> Specific Date
                  </label>
                </div>
              </div>
              {#if extendMode === "days"}
                <div class="form-group">
                  <label for="days">Days to Extend</label>
                  <input id="days" type="number" min="1" bind:value={extendDays} class="form-input" />
                </div>
              {:else}
                <div class="form-group">
                  <label for="extendDate">New Expiration Date</label>
                  <input id="extendDate" type="datetime-local" bind:value={extendDate} class="form-input" />
                </div>
              {/if}
              <button class="submit-btn trial" on:click={handleExtendTrial} disabled={trialLoading}>
                {#if trialLoading}
                  <span class="btn-spinner"></span> Extending...
                {:else}
                  Extend Trial
                {/if}
              </button>
              {/if}
            </div>
          {/if}
        {/if}
      </div>

      {#if $canAccess.admin_management}
        <div class="card danger-zone">
          <h2>Danger zone</h2>
          <p class="danger-zone-copy">This workspace will be hidden from lists. This action cannot be undone.</p>
          <button class="action-btn danger" on:click={() => { showDeleteConfirm = true; deleteError = null; }}>
            Delete workspace
          </button>
        </div>
      {/if}
    {/if}

    {#if activeTab === "members"}
      <div class="card">
        <h2>Members</h2>
        {#if members.length > 0}
          <div class="members-list">
            {#each members as member}
              <div class="member-item">
                <div class="member-main">
                  <div class="member-avatar-badge">
                    <div class="member-avatar">
                      {#if member.avatar_url}
                        <img src={member.avatar_url} alt={member.name} />
                      {:else}
                        <div class="avatar-placeholder">
                          {(member.name && member.name.length > 0) ? member.name.charAt(0) : '?'}
                        </div>
                      {/if}
                    </div>
                    <span class="role-badge" class:badge-owner={member.workspace_role === 'owner'} class:badge-viewer={member.workspace_role === 'viewer'}>
                      {member.workspace_role.charAt(0).toUpperCase() + member.workspace_role.slice(1)}
                    </span>
                  </div>
                  <div class="member-info">
                    <div class="member-name">
                      <a href="#" class="user-link" on:click|preventDefault={() => handleUserClick(member.user_id)}>{member.name}</a>
                    </div>
                    <div class="member-email">
                      <a href="#" class="user-link" on:click|preventDefault={() => handleUserClick(member.user_id)}>{member.email}</a>
                    </div>
                    {#if member.artist_name}
                      <div class="member-artist-name">{member.artist_name}</div>
                    {/if}
                    {#if member.occupation}
                      <div class="member-occupation">{member.occupation}</div>
                    {/if}
                    {#if member.latest_activity}
                      <div class="member-latest-activity">Latest activity: {formatDate(member.latest_activity)}</div>
                    {/if}
                    {#if member.latest_login}
                      <div class="member-latest-login">Last login: {formatDate(member.latest_login)}</div>
                    {/if}
                  </div>
                </div>
              </div>
            {/each}
          </div>
        {:else}
          <div class="empty-state">No members found</div>
        {/if}
      </div>
    {/if}

    {#if activeTab === "audio"}
    <div class="card audio-list-card">
      <h2>Audio Files</h2>
      {#if audioList.length > 0}
        <div class="audio-list">
          {#each audioList as audio}
            <div class="audio-item" on:click={() => handleAudioClick(audio)}>
              <div class="audio-info">
                <div class="audio-title">{audio.title}</div>
                {#if audio.uploader}
                  <div class="audio-uploader">
                    Uploaded by: <a href="#" class="user-link" on:click|preventDefault={() => handleUserClick(audio.uploader.user_id)}>{audio.uploader.name}</a>
                    {#if audio.uploader.email}
                      <span class="audio-uploader-email"> ({audio.uploader.email})</span>
                    {/if}
                  </div>
                {/if}
                <div class="audio-meta">
                  <span class="audio-duration">{formatDuration(audio.duration)}</span>
                  <span class="audio-size">{(audio.size / 1024 / 1024).toFixed(1)} MB</span>
                  <span class="audio-format">{audio.metadata_format}</span>
                </div>
              </div>
              <div class="audio-upload-info">
                <span class="upload-date">Uploaded: {formatDate(audio.uploaded_at)}</span>
              </div>
            </div>
          {/each}
        </div>
      {:else}
        <div class="empty-state">No audio files found</div>
      {/if}
    </div>
    {/if}

    {#if showAudioModal && selectedAudio}
      <div class="modal-overlay" on:click={closeModal}>
        <div class="modal-content" on:click|stopPropagation>
          <div class="modal-header">
            <h2>{selectedAudio.title}</h2>
            <button class="close-button" on:click={closeModal}>×</button>
          </div>
          <div class="modal-body">
            <div class="audio-details-grid">
              <div class="detail-section">
                <h3>Basic Information</h3>
                <div class="detail-row">
                  <span class="label">Title:</span>
                  <span class="value">{selectedAudio.title}</span>
                </div>
                <div class="detail-row">
                  <span class="label">Duration:</span>
                  <span class="value">{formatDuration(selectedAudio.duration)}</span>
                </div>
                <div class="detail-row">
                  <span class="label">Size:</span>
                  <span class="value">{(selectedAudio.size / 1024 / 1024).toFixed(1)} MB</span>
                </div>
                <div class="detail-row">
                  <span class="label">Format:</span>
                  <span class="value">{selectedAudio.metadata_format}</span>
                </div>
                <div class="detail-row">
                  <span class="label">Bitrate:</span>
                  <span class="value">{selectedAudio.metadata_bitrate / 1000} kbps</span>
                </div>
                <div class="detail-row">
                  <span class="label">Sample Rate:</span>
                  <span class="value">{selectedAudio.metadata_sample_rate} Hz</span>
                </div>
                <div class="detail-row">
                  <span class="label">Channels:</span>
                  <span class="value">{selectedAudio.metadata_channels}</span>
                </div>
              </div>

              <div class="detail-section">
                <h3>Metadata</h3>
                <div class="detail-row">
                  <span class="label">Album:</span>
                  <span class="value">{selectedAudio.metadata_album || '-'}</span>
                </div>
                <div class="detail-row">
                  <span class="label">Artist:</span>
                  <span class="value">{selectedAudio.metadata_artist || '-'}</span>
                </div>
                <div class="detail-row">
                  <span class="label">Genre:</span>
                  <span class="value">{selectedAudio.metadata_genre || '-'}</span>
                </div>
                <div class="detail-row">
                  <span class="label">Year:</span>
                  <span class="value">{selectedAudio.metadata_year || '-'}</span>
                </div>
                <div class="detail-row">
                  <span class="label">BPM:</span>
                  <span class="value">{selectedAudio.metadata_bpm || '-'}</span>
                </div>
              </div>

              <div class="detail-section">
                <h3>Upload Information</h3>
                <div class="detail-row">
                  <span class="label">Uploaded by:</span>
                  <span class="value">{selectedAudio.uploader.name}</span>
                </div>
                <div class="detail-row">
                  <span class="label">Upload date:</span>
                  <span class="value">{formatDate(selectedAudio.uploaded_at)}</span>
                </div>
                <div class="detail-row">
                  <span class="label">Created at:</span>
                  <span class="value">{formatDate(selectedAudio.created_at)}</span>
                </div>
                <div class="detail-row">
                  <span class="label">Original filename:</span>
                  <span class="value">{selectedAudio.original_filename}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    {/if}

    {#if showDeleteConfirm}
      <div class="modal-overlay" on:click={closeDeleteConfirm}>
        <div class="modal-content delete-confirm-modal" on:click|stopPropagation>
          <div class="modal-header">
            <h2>Delete workspace</h2>
            <button class="close-button" on:click={closeDeleteConfirm} disabled={deleteLoading}>×</button>
          </div>
          <div class="modal-body">
            <p class="delete-confirm-message">Are you sure? This workspace will be hidden from lists. This action cannot be undone.</p>
            {#if deleteError}
              <div class="delete-error">{deleteError}</div>
            {/if}
            <div class="delete-confirm-actions">
              <button class="action-btn secondary" on:click={closeDeleteConfirm} disabled={deleteLoading}>Cancel</button>
              <button class="action-btn danger" on:click={handleDeleteWorkspace} disabled={deleteLoading}>
                {#if deleteLoading}
                  <span class="btn-spinner"></span> Deleting...
                {:else}
                  Delete
                {/if}
              </button>
            </div>
          </div>
        </div>
      </div>
    {/if}

    {#if pendingExtension}
      <div class="modal-overlay" on:click={cancelExtension}>
        <div class="modal-content confirm-extension-modal" on:click|stopPropagation>
          <div class="modal-header">
            <h2>Confirm Extension</h2>
            <button class="close-button" on:click={cancelExtension} disabled={trialLoading}>×</button>
          </div>
          <div class="modal-body">
            <p class="confirm-extension-message">
              Are you sure you want to extend this trial by
              <strong>{pendingExtension.label.replace(/^\+/, "")}</strong>?
            </p>
            {#if upgradeError}
              <div class="delete-error">{upgradeError}</div>
            {/if}
            <div class="delete-confirm-actions">
              <button class="action-btn secondary" on:click={cancelExtension} disabled={trialLoading}>Cancel</button>
              <button class="submit-btn trial confirm-extend-btn" on:click={confirmExtension} disabled={trialLoading}>
                {#if trialLoading}
                  <span class="btn-spinner"></span> Extending...
                {:else}
                  Confirm
                {/if}
              </button>
            </div>
          </div>
        </div>
      </div>
    {/if}
  {:else}
    <div class="empty-state">
      <div class="empty-message">Workspace not found</div>
    </div>
  {/if}
</div>
{/if}

<style>
  .container {
    padding: 0 0 2.5rem;
    max-width: 1200px;
    margin: 0 auto;
  }

  /* ===== Sticky identity header ===== */
  .ud-header {
    position: sticky;
    top: 0;
    z-index: 20;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: saturate(180%) blur(8px);
    border: 1px solid #eef0f3;
    border-radius: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    padding: 1rem 1.5rem;
    margin-bottom: 1.25rem;
  }
  .ud-identity {
    display: flex;
    align-items: center;
    gap: 1.25rem;
    min-width: 0;
  }
  .ud-avatar {
    width: 64px;
    height: 64px;
    border-radius: 14px;
    overflow: hidden;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .ud-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .ud-avatar .avatar-placeholder {
    font-size: 2rem;
    border-radius: 0;
  }
  .ud-identity-text {
    min-width: 0;
  }
  .ud-name {
    font-weight: 700;
    font-size: 1.35rem;
    color: #111827;
    line-height: 1.2;
  }
  .ud-sub {
    color: #6b7280;
    font-size: 0.95rem;
    margin-top: 0.1rem;
  }
  .ud-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin-top: 0.5rem;
  }
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    background: #eef2f7;
    color: #475569;
    border-radius: 9999px;
    padding: 0.2rem 0.65rem;
    font-size: 0.78rem;
    font-weight: 600;
    text-decoration: none;
  }
  .chip-ok {
    background: #e6f9ed;
    color: #16a34a;
  }
  .chip-warn {
    background: #fef3c7;
    color: #b45309;
  }

  /* ===== Tabs (mirrors UserDetail) ===== */
  .ud-tabs {
    display: flex;
    gap: 0.25rem;
    border-bottom: 1px solid #e5e7eb;
    margin-bottom: 1.75rem;
    overflow-x: auto;
  }
  .ud-tab {
    appearance: none;
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    padding: 0.7rem 1rem;
    font-size: 0.95rem;
    font-weight: 600;
    color: #6b7280;
    cursor: pointer;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    transition: color 0.15s, border-color 0.15s;
  }
  .ud-tab:hover {
    color: #111827;
  }
  .ud-tab:focus {
    outline: none;
  }
  .ud-tab:focus-visible {
    outline: 2px solid #93c5fd;
    outline-offset: -2px;
    border-radius: 6px;
  }
  .ud-tab.active {
    color: #2563eb;
    border-bottom-color: #2563eb;
  }
  .tab-count {
    background: #eef2f7;
    color: #64748b;
    border-radius: 9999px;
    font-size: 0.72rem;
    font-weight: 700;
    padding: 0.05rem 0.45rem;
    min-width: 1.25rem;
    text-align: center;
  }
  .ud-tab.active .tab-count {
    background: #dbeafe;
    color: #2563eb;
  }

  .main-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 2rem;
    margin-bottom: 1.5rem;
  }

  .card {
    background: #fff;
    border-radius: 16px;
    padding: 1.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    border: 1px solid #eef0f3;
    min-width: 0;
    margin-bottom: 1.5rem;
  }
  .card h2 {
    font-size: 1rem;
    font-weight: 700;
    color: #111827;
    margin: 0 0 1rem;
    padding-bottom: 0.6rem;
    border-bottom: 1px solid #f1f5f9;
  }

  .subscription-info {
    display: flex;
    flex-direction: column;
  }

  .info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    padding: 0.55rem 0;
    border-bottom: 1px solid #f1f5f9;
    font-size: 0.95rem;
  }
  .info-row:last-child { border-bottom: none; }
  .label { color: #6b7280; font-weight: 500; }
  .value { color: #111827; font-weight: 600; text-align: right; word-break: break-word; }

  .status-badge {
    display: inline-flex;
    padding: 0.2rem 0.65rem;
    border-radius: 9999px;
    font-size: 0.78rem;
    font-weight: 600;
    background: #eef2f7;
    color: #475569;
    text-transform: lowercase;
  }
  .status-badge.status-active {
    background: #e6f9ed;
    color: #16a34a;
  }
  .status-badge.status-cancelled {
    background: #fef2f2;
    color: #dc2626;
  }

  .trial-badge {
    margin-left: 0.5rem;
    padding: 0.15rem 0.55rem;
    border-radius: 9999px;
    font-size: 0.7rem;
    font-weight: 600;
    background: #fef3c7;
    color: #b45309;
    text-transform: uppercase;
    letter-spacing: 0.025em;
  }

  .members-list {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }
  .member-item {
    display: flex;
    flex-direction: column;
    background: #f7f9fb;
    border-radius: 14px;
    padding: 1.5rem 1.25rem;
    box-shadow: 0 1px 2px rgba(0,0,0,0.02);
    gap: 0.75rem;
    min-height: 100px;
  }
  .member-main {
    display: flex;
    align-items: center;
    gap: 1.25rem;
    min-width: 0;
    flex: 1;
  }
  .member-avatar-badge {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
  }
  .member-avatar {
    width: 56px;
    height: 56px;
    border-radius: 10px;
    overflow: hidden;
    flex-shrink: 0;
    background: #fff;
    box-shadow: 0 1px 2px rgba(0,0,0,0.04);
  }
  .member-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .avatar-placeholder {
    width: 100%;
    height: 100%;
    background: #eef0f3;
    color: #475569;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    font-weight: 600;
    border-radius: 10px;
  }
  .member-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
  }
  .member-name {
    font-weight: 600;
    color: #111827;
    font-size: 1.08rem;
    margin-bottom: 0.1rem;
  }
  .member-email {
    font-size: 0.97rem;
    color: #6b7280;
    margin-bottom: 0.1rem;
  }
  .member-latest-login {
    font-size: 0.93rem;
    color: #1d4ed8;
    margin-bottom: 0.1rem;
  }
  .member-artist-name {
    font-size: 0.97rem;
    color: #475569;
    margin-bottom: 0.1rem;
  }
  .member-occupation {
    font-size: 0.97rem;
    color: #6b7280;
  }
  .role-badge {
    padding: 0.25rem 0.85rem;
    border-radius: 9999px;
    font-size: 0.78rem;
    font-weight: 600;
    background: #eef2f7;
    color: #475569;
    text-transform: capitalize;
    margin-bottom: 0;
    align-self: center;
  }
  .role-badge.badge-owner {
    background: #dbeafe;
    color: #2563eb;
  }
  .role-badge.badge-viewer {
    background: #f1f5f9;
    color: #475569;
  }

  .audio-list-card {
    grid-column: 1 / -1;
    margin-top: 0.5rem;
  }
  .audio-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .audio-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    background: #f7f9fb;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 1px 2px rgba(0,0,0,0.02);
  }
  .audio-item:hover {
    background: #f1f5f9;
  }
  .audio-info {
    flex: 1;
    min-width: 0;
  }
  .audio-title {
    font-weight: 500;
    color: #111827;
    margin-bottom: 0.25rem;
    font-size: 1rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .audio-meta {
    display: flex;
    gap: 1rem;
    color: #6b7280;
    font-size: 0.95rem;
  }
  .audio-upload-info {
    color: #6b7280;
    font-size: 0.95rem;
    min-width: 140px;
    text-align: right;
  }

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem;
    color: #475569;
  }

  .spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid rgba(37, 99, 235, 0.3);
    border-radius: 50%;
    border-top-color: #2563eb;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem;
    text-align: center;
  }

  .error-message {
    color: #dc2626;
    margin-bottom: 1rem;
  }

  .retry-button {
    padding: 0.625rem 1.25rem;
    background: #2563eb;
    color: white;
    border: none;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s ease;
  }

  .retry-button:hover {
    background: #1d4ed8;
  }

  .empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4rem;
    color: #6b7280;
    font-size: 1.125rem;
  }

  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }

  .modal-content {
    background: white;
    border-radius: 12px;
    width: 90%;
    max-width: 800px;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem;
    border-bottom: 1px solid #eef0f3;
  }

  .modal-header h2 {
    margin: 0;
    font-size: 1.5rem;
    color: #111827;
  }

  .close-button {
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #6b7280;
    cursor: pointer;
    padding: 0.5rem;
    line-height: 1;
    border-radius: 4px;
  }

  .close-button:hover {
    background: #f7fafc;
    color: #111827;
  }

  .modal-body {
    padding: 1.5rem;
  }

  .delete-confirm-message {
    margin: 0 0 1rem;
    color: #111827;
  }
  .delete-error {
    padding: 0.75rem;
    margin-bottom: 1rem;
    background: #fef2f2;
    color: #dc2626;
    border-radius: 6px;
    font-size: 0.875rem;
  }
  .delete-confirm-actions {
    display: flex;
    gap: 0.75rem;
    justify-content: flex-end;
    margin-top: 1rem;
  }

  .audio-details-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .detail-section {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .detail-section h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.5rem 0;
  }

  .detail-row {
    display: flex;
    justify-content: space-between;
    padding: 0.5rem 0;
    border-bottom: 1px solid #eef0f3;
  }

  .detail-row:last-child {
    border-bottom: none;
  }

  .detail-row .label {
    color: #6b7280;
    font-size: 0.875rem;
  }

  .detail-row .value {
    color: #111827;
    font-weight: 600;
    text-align: right;
  }

  @media (max-width: 900px) {
    .main-grid {
      grid-template-columns: 1fr;
      gap: 1.5rem;
    }
    .audio-list-card {
      margin-top: 1.5rem;
    }
  }
  @media (max-width: 768px) {
    .container {
      padding: 1rem 0.25rem;
    }
    .ud-header {
      margin-bottom: 1.5rem;
    }
    .ud-identity {
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;
    }
    .main-grid {
      grid-template-columns: 1fr;
      gap: 1.5rem;
    }
    .card {
      padding: 1.25rem 0.75rem 1rem 0.75rem;
    }
    .audio-list-card {
      margin-top: 1.5rem;
    }
  }

  .workspace-info-card {
    margin-bottom: 2rem;
  }
  .info-grid {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .features-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  .feature-badge {
    display: inline-flex;
    background: #e0edff;
    color: #2563eb;
    border-radius: 9999px;
    padding: 0.2rem 0.65rem;
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.01em;
  }

  .user-link {
    color: #2563eb;
    text-decoration: underline;
    cursor: pointer;
  }
  .user-link:hover {
    color: #174ea6;
  }

  .audio-uploader {
    font-size: 0.95rem;
    color: #2563eb;
    margin-bottom: 0.2rem;
  }
  .audio-uploader-email {
    color: #6b7280;
    font-size: 0.93rem;
  }

  /* Upgrade form styles */
  .subscription-card {
    display: flex;
    flex-direction: column;
  }

  .alert {
    padding: 0.75rem 1rem;
    border-radius: 8px;
    margin-bottom: 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.9rem;
  }
  .alert-success {
    background: #e6f9ed;
    color: #276749;
    border: 1px solid #9ae6b4;
  }
  .alert-error {
    background: #fef2f2;
    color: #dc2626;
    border: 1px solid #fecaca;
  }
  .dismiss-btn {
    background: none;
    border: none;
    font-size: 1.25rem;
    cursor: pointer;
    opacity: 0.6;
    color: inherit;
    line-height: 1;
  }
  .dismiss-btn:hover {
    opacity: 1;
  }

  .no-subscription {
    color: #6b7280;
    font-size: 0.95rem;
    padding: 1rem 0;
  }

  .danger-zone {
    border-color: #fecaca;
    background: #fffaf0;
  }
  .danger-zone h2 {
    color: #dc2626;
  }
  .danger-zone-copy {
    color: #744210;
    font-size: 0.9rem;
    margin: 0.5rem 0 1rem;
  }

  .upgrade-actions {
    display: flex;
    gap: 0.75rem;
    margin-top: 1.25rem;
    padding-top: 1rem;
    border-top: 1px solid #eef0f3;
  }

  .action-btn {
    padding: 0.5rem 1rem;
    border: 1px solid #2563eb;
    background: white;
    color: #2563eb;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  .action-btn:hover {
    background: #ebf8ff;
  }
  .action-btn.active {
    background: #2563eb;
    color: white;
  }
  .action-btn.secondary {
    border-color: #48bb78;
    color: #48bb78;
  }
  .action-btn.secondary:hover {
    background: #f0fff4;
  }
  .action-btn.secondary.active {
    background: #48bb78;
    color: white;
  }
  .action-btn.danger {
    border-color: #dc2626;
    color: #dc2626;
  }
  .action-btn.danger:hover:not(:disabled) {
    background: #fef2f2;
  }
  .action-btn.danger:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .upgrade-form {
    margin-top: 1.25rem;
    padding: 1.25rem;
    background: #f8fafc;
    border-radius: 10px;
    border: 1px solid #eef0f3;
  }
  .upgrade-form h3 {
    font-size: 1rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 1rem;
  }
  .shortcut-row {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 1rem;
  }
  .shortcut-button {
    padding: 6px 12px;
    border: 1px solid #4caf50;
    border-radius: 6px;
    background: transparent;
    color: #4caf50;
    cursor: pointer;
    font-size: 0.9rem;
  }
  .shortcut-button:hover:not(:disabled) {
    background: #4caf50;
    color: #fff;
  }
  .shortcut-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .extension-info {
    margin-top: 1rem;
    font-size: 0.9rem;
    color: var(--ds-text, #111827);
  }
  .extension-info .ext-line {
    margin-bottom: 0.25rem;
  }
  .ext-note {
    margin-top: 0.75rem;
    font-size: 0.85rem;
    color: var(--ds-text-muted, #6b7280);
  }
  .confirm-extension-modal {
    max-width: 440px;
  }
  .confirm-extension-message {
    color: var(--ds-text, #111827);
    font-size: 0.95rem;
    margin: 0 0 1.25rem;
  }
  .confirm-extend-btn {
    width: auto;
  }

  .form-group {
    margin-bottom: 1rem;
  }
  .form-group label {
    display: block;
    font-size: 0.8rem;
    font-weight: 500;
    color: #475569;
    margin-bottom: 0.375rem;
  }
  .form-input, .form-select {
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.9rem;
    background: white;
    transition: all 0.2s ease;
  }
  .form-input:focus, .form-select:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
  }
  .form-hint {
    display: block;
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 0.25rem;
  }

  .radio-group {
    display: flex;
    gap: 1.25rem;
  }
  .radio-label {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.9rem;
    color: #475569;
    cursor: pointer;
  }

  .submit-btn {
    width: 100%;
    padding: 0.625rem;
    background: #2563eb;
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin-top: 0.5rem;
  }
  .submit-btn:hover:not(:disabled) {
    background: #1d4ed8;
  }
  .submit-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .submit-btn.trial {
    background: #48bb78;
  }
  .submit-btn.trial:hover:not(:disabled) {
    background: #38a169;
  }

  .btn-spinner {
    width: 0.9rem;
    height: 0.9rem;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: white;
    animation: spin 0.8s linear infinite;
  }
</style> 